/**
 * Button Component
 * Reusable button with consistent styling and variants
 * Extracted from multiple components to eliminate duplication
 */

import React from 'react';

/**
 * Button Component
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Button content
 * @param {Function} props.onClick - Click handler
 * @param {string} props.variant - Button variant ('primary', 'secondary', 'success', 'danger')
 * @param {string} props.size - Button size ('small', 'medium', 'large')
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {boolean} props.loading - Whether button is in loading state
 * @param {Object} props.style - Additional styles
 * @param {string} props.type - Button type (default: 'button')
 * @param {string} props.className - Additional CSS class
 */
const Button = ({
    children,
    onClick,
    variant = 'primary',
    size = 'medium',
    disabled = false,
    loading = false,
    style = {},
    type = 'button',
    className = ''
}) => {
    const variants = {
        primary: {
            backgroundColor: 'var(--border)',
            color: '#ffffff',
            hoverColor: 'var(--bg)'
        },
        secondary: {
            backgroundColor: '#6c757d',
            color: '#ffffff',
            hoverColor: '#5a6268'
        },
        success: {
            backgroundColor: '#27ae60',
            color: '#ffffff',
            hoverColor: '#229954'
        },
        danger: {
            backgroundColor: '#e74c3c',
            color: '#ffffff',
            hoverColor: '#c0392b'
        },
        warning: {
            backgroundColor: '#d97706',
            color: '#ffffff',
            hoverColor: '#b45309'
        }
    };

    const sizes = {
        small: {
            padding: '0.4rem 0.8rem',
            fontSize: '0.8rem',
            borderRadius: '4px'
        },
        medium: {
            padding: '0.6rem 1.5rem',
            fontSize: '0.95rem',
            borderRadius: '6px'
        },
        large: {
            padding: '0.8rem 2rem',
            fontSize: '1rem',
            borderRadius: '8px'
        }
    };

    const baseStyle = {
        border: 'none',
        fontWeight: '500',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
        outline: 'none',
        opacity: disabled || loading ? 0.6 : 1,
        ...sizes[size],
        ...variants[variant],
        ...style
    };

    const [isHovering, setIsHovering] = React.useState(false);

    const handleMouseEnter = () => {
        if (!disabled && !loading) {
            setIsHovering(true);
        }
    };

    const handleMouseLeave = () => {
        setIsHovering(false);
    };

    const handleClick = (e) => {
        if (!disabled && !loading && onClick) {
            onClick(e);
        }
    };

    const buttonStyle = {
        ...baseStyle,
        backgroundColor: isHovering && !disabled && !loading
            ? variants[variant].hoverColor
            : variants[variant].backgroundColor
    };

    return (
        <button
            type={type}
            onClick={handleClick}
            disabled={disabled || loading}
            style={buttonStyle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={className}
        >
            {loading ? 'Loading...' : children}
        </button>
    );
};

export default Button;

